import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Font,
  StyleSheet,
  Image
} from '@react-pdf/renderer';
import moment from 'moment/moment';

import InterRegular from '../../../assets/fonts/Inter-Regular.ttf';
import InterBold from '../../../assets/fonts/Inter-Bold.ttf';

import {
  billOfMaterialItemTypes,
  caseChecklistStatuses,
  itemTypeOptions,
} from '../../../constants/enums';

const styles = StyleSheet.create({
  container: {
    padding: 36,
    fontFamily: 'Inter',
    fontWeight: 'normal',
    fontSize: 7,
    color: '#202124',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between'
  },
  titleBlock: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
  },
  logo: {
    height: 20,
    width: 'auto',
  },
  kitLogo: {
    height: 20,
    width: 'auto'
  },
  table: {
    marginTop: 16,
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: '0.5px solid #DDDDDD',
  },
  tableCell: {
    padding: 2,
    justifyContent: 'center',
    fontFamily: 'Inter',
  },
  sectionTable: {
    marginTop: 8,
    paddingBottom: 8,
  },
  sectionTableHeader: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    fontWeight: 'bold',
    fontFamily: 'Inter',
    paddingTop: 4,
    paddingBottom: 4,
    borderTop: '0.5px solid #000000',
    borderBottom: '0.5px solid #000000',
  },
  tableHeaderCell: {
    padding: 2,
    display: 'flex',
    justifyContent: 'center',
    fontWeight: 'bold',
    fontFamily: 'Inter',
  },
  tableRowLast: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: '0.5px solid #000000',
  },
  borderCell: {
    border: '0.5px solid #969696',
    borderRadius: '4px',
    height: '13px',
    width: '13px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  notesTableRow: {
    borderBottom: '0.5px solid #DDDDDD',
    paddingTop: '4px',
    paddingBottom: '4px',
  },
  notesContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '4px',
    fontFamily: 'Inter',
    fontWeight: 'normal',
  },
  activityBlock: {
    marginTop: 8,
    paddingBottom: 4,
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    borderBottom: '0.5px solid #000000'
  }
});

Font.register({
  family: 'Inter',
  fonts: [
    {
      src: InterRegular,
      fontWeight: 'normal'
    },
    {
      src: InterBold,
      fontWeight: 'bold',
    },
  ],
});

const getItemTypeLabel = (type) => {
  if (!type) {
    return '';
  }

  if (itemTypeOptions[type]) {
    return itemTypeOptions[type].label.toUpperCase();
  }

  return String(type).toUpperCase();
};

const UdiReferencePDF = (props) => {
  const {
    activeCase,
    tenant,
    hospital,
    surgeon,
    kits = [],
    checklists = [],
    caseActivity = [],
    users = [],
    withItems = false,
    items = [],
    itemDetails = [],
  } = props;

  const getKitLogo = (kitId) => {
    const kit = kits?.find((k) => k.kitId === kitId);
    return kit?.logo?.downloadUrl || '';
  };

  const getKitVersion = (kitId) => {
    const kit = kits?.find((k) => k.kitId === kitId);
    return kit?.version || '';
  };

  const getItemReference = (itemId) => itemDetails?.find((item) => item.id === itemId) || null;

  const getGtinValue = (item) => {
    const reference = getItemReference(item.id);
    return reference?.gtin || item?.gtin || '';
  };

  const getRebateValue = (item) => {
    const reference = getItemReference(item.id);
    return reference?.rebateCode || item?.rebateCode || '';
  };

  const getTypeValue = (item) => {
    const reference = getItemReference(item.id);
    return getItemTypeLabel(reference?.type || item?.type || '');
  };

  const renderActivityView = (checklist) => {
    if (!activeCase) {
      return null;
    }

    const activity = caseActivity.find((entry) => entry.arguments && entry.arguments.checklistId === checklist.id);
    if (!activity) {
      return null;
    }

    const user = users?.find((entry) => entry.uid === activity.userId);

    return (
      <View style={styles.activityBlock}>
        <View style={{ display: 'flex', flexDirection: 'row' }}>
          <Text style={{ fontWeight: 'bold' }}>INSPECTION PASSED</Text>
          <View style={{ marginLeft: 8, display: 'flex', flexDirection: 'row' }}>
            <Text>
              {`${moment(activity.createdAt).format('Do MMMM YYYY')}  |  ${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
            </Text>
          </View>
        </View>
        <Text>{activeCase?.id}</Text>
      </View>
    );
  };

  const renderTableHeader = () => (
    <View style={styles.sectionTableHeader}>
      <View style={{ ...styles.tableHeaderCell, width: '15%' }}><Text>ITEM #</Text></View>
      <View style={{ ...styles.tableHeaderCell, width: '18%' }}><Text>UDI</Text></View>
      <View style={{ ...styles.tableHeaderCell, width: '33%' }}><Text>DESCRIPTION</Text></View>
      <View style={{ ...styles.tableHeaderCell, width: '14%' }}><Text>ITEM TYPE</Text></View>
      <View style={{ ...styles.tableHeaderCell, width: '9%' }}><Text>REBATE</Text></View>
      <View style={{ ...styles.tableHeaderCell, width: '5%' }}><Text style={{ textAlign: 'center' }}>KIT QTY</Text></View>
      <View style={{ ...styles.tableHeaderCell, width: '6%' }}><Text style={{ textAlign: 'center' }}>CO. OUT</Text></View>
    </View>
  );

  const renderNotesRow = (item, key) => (
    <View key={key} style={styles.notesTableRow}>
      <View style={{ ...styles.notesContainer, border: `1px solid ${item.color || '#F58634'}` }}>
        <View style={{ width: '5%' }}><Text></Text></View>
        <View style={{ width: '90%' }}>
          <Text style={{ color: `${item.color || '#F58634'}` }}>
            <Text style={{ textTransform: 'uppercase' }}>
              {`${item.title}  `}
            </Text>
            <Text style={{ fontWeight: 'normal', fontFamily: 'Inter' }}>{item.note}</Text>
          </Text>
        </View>
        <View style={{ width: '5%' }}><Text></Text></View>
      </View>
    </View>
  );

  const renderItemRow = (item, isLastRow = false, outboundValue = item.outbound) => (
    <View key={item.id} style={isLastRow ? styles.tableRowLast : styles.tableRow} wrap={false}>
      <View style={{ ...styles.tableCell, width: '15%' }}><Text>{item.code || item.name || ''}</Text></View>
      <View style={{ ...styles.tableCell, width: '18%' }}><Text>{getGtinValue(item)}</Text></View>
      <View style={{ ...styles.tableCell, width: '33%' }}><Text>{item.checklistDescription || item.description || ''}</Text></View>
      <View style={{ ...styles.tableCell, width: '14%' }}><Text>{getTypeValue(item)}</Text></View>
      <View style={{ ...styles.tableCell, width: '9%' }}><Text>{getRebateValue(item)}</Text></View>
      <View style={{ ...styles.tableCell, width: '5%' }}><Text style={{ textAlign: 'center', color: '#969696' }}>{item.quantity}</Text></View>
      <View style={{ ...styles.tableCell, alignItems: 'center', width: '6%' }}>
        <View style={styles.borderCell}>
          <Text
            style={{
              textAlign: 'center',
              color: outboundValue < item.quantity ? '#F58634' : '#202124'
            }}
          >
            {outboundValue || 0}
          </Text>
        </View>
      </View>
    </View>
  );

  return (
    <Document>
      {checklists?.map((checklist) => (
        <Page key={checklist.id} size="A4" style={styles.container}>
          <View>
            <View style={styles.titleBlock}>
              <View>
                {tenant.documentLogo && (
                  <Image
                    src={tenant?.documentLogo?.downloadUrl}
                    style={styles.logo}
                  />
                )}
              </View>
              <View>
                {!!getKitLogo(checklist.kitId) && (
                  <Image
                    src={getKitLogo(checklist.kitId)}
                    style={styles.kitLogo}
                  />
                )}
              </View>
            </View>

            <View style={styles.table}>
              <View style={styles.tableRow}>
                <View style={{ ...styles.tableCell, width: '10%', fontWeight: 'bold' }}><Text>SURGERY</Text></View>
                <View style={{ ...styles.tableCell, width: '40%' }}><Text>{activeCase ? moment(activeCase.date).format('Do MMMM YYYY') : ''}</Text></View>
                <View style={{ ...styles.tableCell, width: '8%', fontWeight: 'bold' }}><Text>KIT ID</Text></View>
                <View style={{ ...styles.tableCell, width: '42%' }}><Text>{checklist?.kitId || ''}</Text></View>
              </View>
              <View style={styles.tableRow}>
                <View style={{ ...styles.tableCell, width: '10%', fontWeight: 'bold' }}><Text>SURGEON</Text></View>
                <View style={{ ...styles.tableCell, width: '40%' }}><Text>{surgeon ? `${surgeon?.title} ${surgeon?.firstName} ${surgeon?.lastName}` : ''}</Text></View>
                <View style={{ ...styles.tableCell, width: '8%', fontWeight: 'bold' }}><Text>NAME</Text></View>
                <View style={{ ...styles.tableCell, width: '42%' }}><Text>{checklist?.kitName || ''}</Text></View>
              </View>
              <View style={styles.tableRow}>
                <View style={{ ...styles.tableCell, width: '10%', fontWeight: 'bold' }}><Text>HOSPITAL</Text></View>
                <View style={{ ...styles.tableCell, width: '40%' }}><Text>{hospital?.name || ''}</Text></View>
                <View style={{ ...styles.tableCell, width: '8%', fontWeight: 'bold' }}><Text>SET #</Text></View>
                <View style={{ ...styles.tableCell, width: '42%' }}><Text>{checklist?.setNumber || ''}</Text></View>
              </View>
              <View style={{ ...styles.tableRow, border: 'none' }}>
                <View style={{ ...styles.tableCell, width: '10%', fontWeight: 'bold' }}><Text>PATIENT ID</Text></View>
                <View style={{ ...styles.tableCell, width: '40%' }}><Text>{activeCase ? activeCase.patientReference : ''}</Text></View>
                <View style={{ ...styles.tableCell, width: '8%', fontWeight: 'bold' }}><Text>STATUS</Text></View>
                <View style={{ ...styles.tableCell, width: '42%', fontWeight: 'bold' }}>
                  {checklist?.status ? (
                    <Text
                      style={{
                        color: checklist.status === caseChecklistStatuses.COMPLETE.value ? caseChecklistStatuses.COMPLETE.color : caseChecklistStatuses.INSPECT.color
                      }}
                    >
                      {caseChecklistStatuses[checklist.status]?.label || ''}
                    </Text>
                  ) : null}
                </View>
              </View>
            </View>

            {checklist.sections?.map((section, index) => (
              <View key={section.id || `${checklist.id}_${index}`} style={{ marginTop: 16 }} break={!!section.printOnNewPage}>
                <View>
                  <Text style={{ fontWeight: 'bold', fontSize: 9 }}>{section.name}</Text>
                  <Text style={{ color: '#969696' }}>{section.subtitle}</Text>

                  <View style={styles.sectionTable}>
                    {renderTableHeader()}

                    {section.items?.map((item, itemIndex) => (
                      item.type === billOfMaterialItemTypes.NOTES.value ? (
                        renderNotesRow(item, item.id || `${section.id || index}_note_${itemIndex}`)
                      ) : (
                        renderItemRow(item, itemIndex === section.items.length - 1)
                      )
                    ))}
                  </View>
                </View>
              </View>
            ))}
          </View>

          <View>
            {renderActivityView(checklist)}
          </View>

          <Text
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            fixed
            style={{ position: 'absolute', bottom: 16, left: 36 }}
          />
          {!!getKitVersion(checklist.kitId) && (
            <Text
              render={() => getKitVersion(checklist.kitId)}
              fixed
              style={{ position: 'absolute', bottom: 16, left: '50%', right: '50%' }}
            />
          )}
          <Text
            render={() => checklist?.kitName || ''}
            fixed
            style={{ position: 'absolute', bottom: 16, right: 36 }}
          />
        </Page>
      ))}
      {withItems && !!items?.length && (
        <Page size="A4" style={styles.container}>
          <View>
            <Text style={{ fontWeight: 'bold', fontSize: 9 }}>Items</Text>
            <Text style={{ color: '#969696' }}>Additional Items Shipped</Text>
            <View style={styles.sectionTable}>
              {renderTableHeader()}

              {items?.map((item, index) => renderItemRow(item, index === items.length - 1, item.quantity))}
            </View>
          </View>
          <View style={styles.activityBlock}>
            <View style={{ display: 'flex', flexDirection: 'row' }}>
              <Text style={{ fontWeight: 'bold' }}>INSPECTION PASSED</Text>
            </View>
            <Text>{activeCase?.id}</Text>
          </View>
          <Text
            render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
            fixed
            style={{ position: 'absolute', bottom: 16, left: 36 }}
          />
        </Page>
      )}
    </Document>
  );
};

export default UdiReferencePDF;
